import { firebaseConfig } from '../firebaseConfig.ts'

/** Firebase Auth localStorage/IDB key (must match main app default instance). */
export function getFirebaseAuthStorageKey(appName = '[DEFAULT]'): string {
  return `firebase:authUser:${firebaseConfig.apiKey}:${appName}`
}

/** Parses uid from Firebase Auth persisted user blob. */
export function parsePersistedAuthUid(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null
  const uid = (value as { uid?: unknown }).uid
  return typeof uid === 'string' && uid.length > 0 ? uid : null
}

export type AuthIdbPersistenceReader = {
  _isAvailable(): Promise<boolean>
  _get<T>(key: string): Promise<T | null>
}

export async function readPersistedAuthUidFromIdb(
  persistence: AuthIdbPersistenceReader,
  appName = '[DEFAULT]',
): Promise<string | null> {
  const result = await readPersistedAuthUidFromIdbSafe(persistence, appName)
  if (result.status === 'error') {
    throw new Error(result.message)
  }
  return result.uid
}

export type PersistedAuthUidReadResult =
  | { status: 'ok'; uid: string | null }
  | { status: 'error'; message: string }

/** Safe IDB read: never throws; use in SW logout guard (fail-open on error). */
export async function readPersistedAuthUidFromIdbSafe(
  persistence: AuthIdbPersistenceReader,
  appName = '[DEFAULT]',
): Promise<PersistedAuthUidReadResult> {
  try {
    const available = await persistence._isAvailable()
    if (!available) return { status: 'ok', uid: null }
    const value = await persistence._get(getFirebaseAuthStorageKey(appName))
    return { status: 'ok', uid: parsePersistedAuthUid(value) }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return { status: 'error', message }
  }
}

const IDB_PREFLIGHT_DELAYS_MS = [0, 50, 100, 200] as const

export type PersistedAuthUidPreflightResult = {
  matched: boolean
  waitMs: number
}

/**
 * Waits until shared IDB auth blob uid matches expectedUid (bounded backoff).
 * Always resolves; caller should still post SW_AUTH_SYNC if unmatched after cap.
 */
export async function waitForPersistedAuthUidMatch(
  expectedUid: string,
  readUid: () => Promise<string | null>,
): Promise<PersistedAuthUidPreflightResult> {
  const start = performance.now()
  for (const delayMs of IDB_PREFLIGHT_DELAYS_MS) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
    const uid = await readUid()
    if (uid === expectedUid) {
      return { matched: true, waitMs: Math.round(performance.now() - start) }
    }
  }
  return { matched: false, waitMs: Math.round(performance.now() - start) }
}
